import React, { useState } from "react";
import axios from "../../axiosConfig";
import { toast } from "react-toastify";
import "./AdminTools.css";
import CompanyAutocomplete from "./CompanyAutocomplete";

const AdminTools = ({ fetchPendingTasks, folderCompanies = [] }) => {
  const [showTools, setShowTools] = useState(false);

  // Folder creation
  const [folderCompanyId, setFolderCompanyId] = useState("");
  const [folderMessage, setFolderMessage] = useState(null);
  const [folderLoading, setFolderLoading] = useState(false);

  // Modals
  const [insiderModalVisible, setInsiderModalVisible] = useState(false);
  const [personnelDataModalVisible, setPersonnelDataModalVisible] = useState(false);

  // Insider
  const [insiderForm, setInsiderForm] = useState({
    company_id: null,
    firstName: "",
    surname: "",
    addBy: "",
    addTime: new Date(
      new Date().getTime() - new Date().getTimezoneOffset() * 60000
    ).toISOString().slice(0, 16),
  });
  const [insiderMessage, setInsiderMessage] = useState(null);
  const [insiderLoading, setInsiderLoading] = useState(false);

  // Personnel
  const [personnelForm, setPersonnelForm] = useState({
    surname: "",
    firstName: "",
    birthSurname: "",
    dateOfBirth: "",
    nationalIDNumber: "",
    privateAddress: "",
    workLandline: "",
    workMobile: "",
    privateLandline: "",
    privateMobile: "",
    employerName: "",
    employerAddress: "",
    email: ""
  });
  const [personnelMessage, setPersonnelMessage] = useState(null);
  const [personnelLoading, setPersonnelLoading] = useState(false);

  const handleCreateFolder = async () => {
    if (!folderCompanyId) {
      setFolderMessage("Please select a company.");
      return;
    }

    setFolderLoading(true);
    setFolderMessage(null);

    try {
      await axios.post("/api/folders/", { company_id: folderCompanyId }, { withCredentials: true });
      const company = folderCompanies.find((c) => String(c.company_id) === folderCompanyId);
      setFolderMessage(`Folder for ${company ? company.bbg.split(' ')[0] : "company"} created successfully.`);
    } catch (error) {
      setFolderMessage(error.response?.data?.error || "Failed to create folder.");
    } finally {
      setFolderLoading(false);
      setFolderCompanyId("");
    }
  };

  const handleRegisterInsider = async () => {
    const { firstName, surname, addBy, addTime } = insiderForm;
    if (!firstName || !surname || !addBy || !addTime) {
      setInsiderMessage("Required fields are empty.");
      return;
    }

    setInsiderLoading(true);
    setInsiderMessage(null);

    try {
      await axios.post("/api/inside/", insiderForm, { withCredentials: true });
      setInsiderMessage("Insider registered.");
      setInsiderForm({
        company_id: null,
        firstName: "",
        surname: "",
        addBy: "",
        addTime: new Date().toISOString().slice(0, 16),
      });
      setInsiderModalVisible(false);
      await fetchPendingTasks(); // Fetch new tasks
    } catch (error) {
      setInsiderMessage(error.response?.data?.error || "Failed to register insider.");
    } finally {
      setInsiderLoading(false);
    }
  };

  const handleInsiderChange = (e) => {
    const { name, value } = e.target;
    setInsiderForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePersonnelSubmit = async () => {
    setPersonnelLoading(true);
    setPersonnelMessage(null);

    try {
      await axios.post("/api/data/", personnelForm, { withCredentials: true });
      setPersonnelMessage("Personnel data submitted successfully.");
      setPersonnelForm({
        surname: "",
        firstName: "",
        birthSurname: "",
        dateOfBirth: "",
        nationalIDNumber: "",
        privateAddress: "",
        workLandline: "",
        workMobile: "",
        privateLandline: "",
        privateMobile: "",
        employerName: "",
        employerAddress: "",
        email: ""
      });
      setPersonnelDataModalVisible(false);
      await fetchPendingTasks(); // Fetch new tasks
    } catch (err) {
      setPersonnelMessage(err.response?.data?.error || "Failed to submit data.");
    } finally {
      setPersonnelLoading(false);
    }
  };

  const handlePersonnelChange = (e) => {
    const { name, value } = e.target;
    setPersonnelForm((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="admin-tools-container">
      <div className="admin-tools-header" onClick={() => setShowTools((prev) => !prev)}>
        <span className="arrow">{showTools ? "▼" : "▶"}</span>
        <h3>Admin Tools</h3>
      </div>

      {showTools && (
        <>
          <form
            className="tool-section"
            onSubmit={(e) => {
              e.preventDefault(); // Prevent page reload
              handleCreateFolder(); // Call your folder creation logic
            }}
          >
            <strong>Create New Company Folder</strong>
            <div className="folder-form-row">
              <select
                value={folderCompanyId}
                onChange={(e) => setFolderCompanyId(e.target.value)}
                required
              >
                <option value="">Select a company...</option>
                {folderCompanies.filter((c) => c.bbg).map((c) => {
                  const tickerVal = c.bbg.split(' ')[0];
                  const label = `${tickerVal} - ${c.company_name_short}${c.folder ? ' (Folder already exists)' : ''}`;
                  return (
                    <option
                      key={c.company_id}
                      value={c.company_id}
                      disabled={c.folder}
                      style={c.folder ? { color: '#999' } : {}}
                    >
                      {label}
                    </option>
                  );
                })}
              </select>
              <button type="submit" disabled={folderLoading}>
                {folderLoading ? "Creating..." : "Create"}
              </button>
            </div>
            {folderMessage && <p className="feedback-message">{folderMessage}</p>}
          </form>

          <div className="tool-section">
            <strong>Insiders Register</strong>
            <div>
              <button onClick={() => setInsiderModalVisible(true)}>Make Person Inside</button>
              <button onClick={() => setPersonnelDataModalVisible(true)}>Log New Prog Staff</button>
            </div>
          </div>
        </>
      )}

      {insiderModalVisible && (
        <div className="modal-overlay">
          <form
            className="modal-box"
            onSubmit={(e) => {
              e.preventDefault();
              handleRegisterInsider();
            }}
          >
            <h3>Register Insider</h3>
            <CompanyAutocomplete
              onSelect={(selectedCompanyId) =>
                setInsiderForm((prev) => ({ ...prev, company_id: selectedCompanyId }))
              }
            />
            <input
              type="text"
              name="firstName"
              placeholder="First Name"
              value={insiderForm.firstName}
              onChange={handleInsiderChange}
              required
            />
            <input
              type="text"
              name="surname"
              placeholder="Surname"
              value={insiderForm.surname}
              onChange={handleInsiderChange}
              required
            />
            <input
              type="text"
              name="addBy"
              placeholder="Made Inside By Whom? (Full Name Required)"
              value={insiderForm.addBy}
              onChange={handleInsiderChange}
              required
            />
            <input
              type="datetime-local"
              name="addTime"
              value={insiderForm.addTime}
              onChange={handleInsiderChange}
              required
            />
            <div className="modal-actions">
              <button type="button" onClick={() => setInsiderModalVisible(false)}>Cancel</button>
              <button type="submit" disabled={insiderLoading}>
                {insiderLoading ? "Registering..." : "Confirm"}
              </button>
            </div>
            {insiderMessage && <p className="feedback-message">{insiderMessage}</p>}
          </form>
        </div>
      )}

      {/* Personnel Modal */}
      {personnelDataModalVisible && (
        <div className="modal-overlay">
          <form
            className="modal-box"
            onSubmit={(e) => {
              e.preventDefault();
              handlePersonnelSubmit();
            }}
          >
            <h3>New Personnel Data</h3>
            {Object.keys(personnelForm).map((field) => {
              const isRequiredField = ["surname", "firstName", "birthSurname", "email"].includes(field);
              return (
                <input
                  key={field}
                  name={field}
                  type={field === "dateOfBirth" ? "date" : "text"}
                  placeholder={field
                    .replace(/([A-Z])/g, " $1")
                    .replace(/^./, (s) => s.toUpperCase())}
                  value={personnelForm[field]}
                  onChange={handlePersonnelChange}
                  required={isRequiredField}
                />
              );
            })}
            <div className="modal-actions">
              <button type="button" onClick={() => setPersonnelDataModalVisible(false)}>Cancel</button>
              <button type="submit" disabled={personnelLoading}>
                {personnelLoading ? "Submitting..." : "Submit"}
              </button>
            </div>
            {personnelMessage && <p className="feedback-message">{personnelMessage}</p>}
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminTools;
